import { useCallback, useEffect, useRef, useState } from "react";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";

const IDLE_MS = 10 * 60 * 1000;
const WARN_MS = 9 * 60 * 1000;
const PING_EVERY_MS = 60 * 1000;
const KEY = "ar_last_activity";
const EVENTS = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"];

const lastActivity = () => Number(localStorage.getItem(KEY) || Date.now());

/** Signs the user out after 10 idle minutes (shared across tabs), warning them at 9 minutes. */
export default function IdleGuard({ onTimeout }) {
  const [left, setLeft] = useState(null);
  const lastPing = useRef(0);
  const warning = left !== null;
  const warningRef = useRef(false);
  warningRef.current = warning;

  const touch = useCallback((force = false) => {
    const now = Date.now();
    localStorage.setItem(KEY, String(now));
    if (force || now - lastPing.current > PING_EVERY_MS) {
      lastPing.current = now;
      api.post("/auth/activity").catch(() => {});
    }
  }, []);

  useEffect(() => {
    touch(true);
    const onAct = () => { if (!warningRef.current) touch(); };
    EVENTS.forEach((e) => window.addEventListener(e, onAct, { passive: true }));
    const t = setInterval(() => {
      const idle = Date.now() - lastActivity();
      if (idle >= IDLE_MS) { clearInterval(t); onTimeout(); }
      else if (idle >= WARN_MS) setLeft(Math.ceil((IDLE_MS - idle) / 1000));
      else setLeft(null);
    }, 1000);
    return () => { EVENTS.forEach((e) => window.removeEventListener(e, onAct)); clearInterval(t); };
  }, [touch, onTimeout]);

  // Detect promptly when this session was replaced by a sign-in elsewhere
  useEffect(() => {
    const t = setInterval(() => api.get("/auth/me").catch(() => {}), 30000);
    return () => clearInterval(t);
  }, []);

  return (
    <AlertDialog open={warning}>
      <AlertDialogContent className="bg-white" data-testid="idle-warning-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Your session is about to expire</AlertDialogTitle>
          <AlertDialogDescription>
            You've been inactive for a while. For your security you'll be signed out in{" "}
            <span className="font-mono font-semibold text-[#0A3622]" data-testid="idle-countdown">{left}s</span>.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onTimeout} data-testid="idle-signout-button">Sign out</AlertDialogCancel>
          <AlertDialogAction onClick={() => { touch(true); setLeft(null); }} data-testid="idle-stay-button">Stay signed in</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
